<template>
  <div v-if="data.length >= 1" class="expiry-section">
    <div class="list-heading">{{ title }}</div>
    
    <!-- Chart with Legend Checkboxes -->
    <div class="chart-container">
      <div class="chart-wrapper">
        <svg viewBox="0 0 200 200" width="200" height="200">
          <circle cx="100" cy="100" r="80" fill="transparent" stroke="#e0e0e0" stroke-width="30"/>
          
          <circle 
            v-if="counts.expired > 0"
            cx="100" cy="100" r="80" 
            fill="transparent" 
            stroke="#dc3545" 
            stroke-width="30"
            :stroke-dasharray="getDashArray(counts.expired)"
            :stroke-dashoffset="0"
            transform="rotate(-90 100 100)"
            class="pie-segment"
            @click="showTooltip($event, 'Expired', counts.expired)"
          >
            <title>{{ getTooltip('Expired', counts.expired) }}</title>
          </circle>
          
          <circle 
            v-if="counts.immediately > 0"
            cx="100" cy="100" r="80" 
            fill="transparent" 
            stroke="#ff6b6b" 
            stroke-width="30"
            :stroke-dasharray="getDashArray(counts.immediately)"
            :stroke-dashoffset="getDashOffset(counts.expired)"
            transform="rotate(-90 100 100)"
            class="pie-segment"
            @click="showTooltip($event, 'Expiring ≤ 7 days', counts.immediately)"
          >
            <title>{{ getTooltip('Expiring ≤ 7 days', counts.immediately) }}</title>
          </circle>
          
          <circle 
            v-if="counts.soon > 0"
            cx="100" cy="100" r="80" 
            fill="transparent" 
            stroke="#ffc107" 
            stroke-width="30"
            :stroke-dasharray="getDashArray(counts.soon)"
            :stroke-dashoffset="getDashOffset(counts.expired + counts.immediately)"
            transform="rotate(-90 100 100)"
            class="pie-segment"
            @click="showTooltip($event, 'Expiring ≤ 30 days', counts.soon)"
          >
            <title>{{ getTooltip('Expiring ≤ 30 days', counts.soon) }}</title>
          </circle>
          
          <text x="100" y="95" text-anchor="middle" font-size="24" font-weight="bold" fill="#333">
            {{ total }}
          </text>
          <text x="100" y="115" text-anchor="middle" font-size="10" fill="#666">
            Total
          </text>
        </svg>
      </div>
      
      <!-- Legend with Checkboxes -->
      <div class="legend">
        <label class="legend-item" v-for="(item, key) in legendItems" :key="key">
          <input type="checkbox" v-model="item.checked" @change="onFilterChange" />
          <span class="legend-color" :style="{ background: item.color }"></span>
          <span>{{ item.label }} ({{ item.count }})</span>
        </label>
      </div>
    </div>

    <!-- Custom Tooltip -->
    <div 
      v-if="tooltipVisible" 
      class="chart-tooltip" 
      :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
    >
      {{ tooltipText }}
    </div>

    <div class="table-wrapper">
      <table class="table text-left table-hover bg-white text-dark">
        <thead>
          <tr>
            <th>Name</th>
            <th>Contact</th>
            <th>ExpiryDate</th>
            <th>Expiry Days</th>
            <th>Doc No.</th>
            <th>Email</th>
          </tr>
        </thead>
        <tr v-for="record in data" :key="record.documentExpiryResultId" :class="getRowClass(record)">
          <td>{{record.officerName}}</td>
          <td>{{record.contact}}</td>
          <td>{{formatDate(record.expiryDate)}}</td>
          <td>{{record.daysUntilExpiry <= 0 ? 'Expired':record.daysUntilExpiry + ' days'}}</td>
          <td>{{record.documentNumber}}</td>
          <td>{{record.email}}</td>
        </tr>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination-container" v-if="endPage > 1">
      <button 
        class="Btn btn-primary btn-sm m-1" 
        v-show="isRangeLess" 
        @click="$emit('prev-page')"
      >&lt;&lt;
      </button>
      <span v-for="pageNo in endPage" :key="'page-' + pageNo">
        <button 
          class="btn btn-success btn-sm"
          :class="{'btn-selected': (pageNo - 1) === currentPage}"
          type="button" 
          v-if="isShownPageButton(pageNo)" 
          @click="$emit('page-change', pageNo - 1)"
        >
          {{ pageNo }}
        </button>
      </span>
      <button 
        type="button" 
        class="Btn btn-primary btn-sm m-1" 
        v-show="isRangeGreater" 
        @click="$emit('next-page')"
      >
        &gt;&gt;
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { formatDate } from '@/Filters/dateFilter';

interface ExpiryRecord {
  documentExpiryResultId: number;
  officerName: string;
  contact: string;
  daysUntilExpiry: number;
  documentNumber: string;
  documentType: string;
  email: string;
  expiryDate: string;
  status: string;
}

interface LegendItem {
  key: string;
  label: string;
  color: string;
  count: number;
  checked: boolean;
}

interface Props {
  data: ExpiryRecord[];
  total: number;
  title: string;
  currentPage: number;
  startPage: number;
  endPage: number;
  isRangeGreater: boolean;
  isRangeLess: boolean;
  labelsPerPage: number;
  expiredCount?: number;
  immediatelyExpiringCount?: number;
  soonExpiringCount?: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'prev-page'): void;
  (e: 'next-page'): void;
  (e: 'page-change', page: number): void;
  (e: 'filter-change', filters: LegendItem[]): void;
}>();

const circumference = 2 * Math.PI * 80;

// Tooltip state
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipX = ref(0);
const tooltipY = ref(0);

const legendItems = ref<LegendItem[]>([
  { key: 'expired', label: 'Expired', color: '#dc3545', count: 0, checked: true },
  { key: 'immediately', label: 'Expiring ≤ 7 days', color: '#ff6b6b', count: 0, checked: true },
  { key: 'soon', label: 'Expiring ≤ 30 days', color: '#ffc107', count: 0, checked: true }
]);

// Update counts when props change
watch(
  [() => props.expiredCount, () => props.immediatelyExpiringCount, () => props.soonExpiringCount],
  () => {
    legendItems.value[0].count = props.expiredCount ?? 0;
    legendItems.value[1].count = props.immediatelyExpiringCount ?? 0;
    legendItems.value[2].count = props.soonExpiringCount ?? 0;
  },
  { immediate: true }
);

const counts = computed(() => ({
  expired: props.expiredCount ?? 0,
  immediately: props.immediatelyExpiringCount ?? 0,
  soon: props.soonExpiringCount ?? 0
}));

const getTooltip = (label: string, count: number): string => {
  if (props.total === 0) return `${label}: 0 (0%)`;
  const percentage = ((count / props.total) * 100).toFixed(1);
  return `${label}: ${count} (${percentage}%)`;
};

const showTooltip = (event: MouseEvent, label: string, count: number) => {
  const percentage = props.total > 0 ? ((count / props.total) * 100).toFixed(1) : 0;
  tooltipText.value = `${label}: ${count} (${percentage}%)`;
  
  // Position tooltip near click/touch
  const rect = (event.target as SVGElement).getBoundingClientRect();
  tooltipX.value = event.clientX - 50;
  tooltipY.value = event.clientY - 40;
  
  tooltipVisible.value = true;
  
  // Auto hide after 3 seconds
  clearTimeout((window as any)._tooltipTimer);
  (window as any)._tooltipTimer = setTimeout(() => {
    tooltipVisible.value = false;
  }, 3000);
};

const onFilterChange = () => {
  emit('filter-change', legendItems.value);
};

const getDashArray = (count: number): string => {
  if (props.total === 0) return '0 502.65';
  const percentage = count / props.total;
  const dashLength = circumference * percentage;
  return `${dashLength} ${circumference - dashLength}`;
};

const getDashOffset = (offsetCount: number): number => {
  if (props.total === 0) return 0;
  const percentage = offsetCount / props.total;
  return -(circumference * percentage);
};

const getRowClass = (record: ExpiryRecord): string => {
  if (record.status === 'Expired') return 'row-expired';
  if (record.daysUntilExpiry <= 7 && record.daysUntilExpiry > 0) return 'row-immediately';
  if (record.daysUntilExpiry < 30 && record.daysUntilExpiry > 7) return 'row-soon';
  return '';
};

const isShownPageButton = (pageNo: number): boolean => {
  return (pageNo > props.startPage) && (pageNo <= (props.startPage + props.labelsPerPage));
};

defineExpose({ legendItems });
</script>

<style scoped>
.expiry-section {
  border: 2px solid #333;
  border-radius: 8px;
  margin-bottom: 20px;
  overflow: hidden;
  background: white;
}

.list-heading {
  background: black;
  font-size: 18px;
  color: white;
  font-weight: bold;
  padding: 8px 12px;
  margin: 0;
}

.chart-container {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 20px;
  flex-wrap: wrap;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  position: relative;
}

.chart-wrapper {
  display: flex;
  justify-content: center;
}

.legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
}

.legend-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
}

/* Custom Tooltip */
.chart-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: tooltipFade 0.2s ease;
  white-space: nowrap;
}

@keyframes tooltipFade {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.table-wrapper {
  padding: 0;
  overflow-x: auto;
}

.table-wrapper table {
  margin-bottom: 0;
}

.pagination-container {
  background: #f8f9fa;
  border-top: 1px solid #ddd;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
}

.row-expired {
  background-color: #dc3545 !important;
  color: white !important;
}

.row-immediately {
  background-color: #ff6b6b !important;
  color: white !important;
}

.row-soon {
  background-color: #ffc107 !important;
  color: #333 !important;
}

.btn-selected {
  background: blue;
  color: white;
}

.pie-segment {
  cursor: pointer;
  transition: filter 0.2s ease;
}

.pie-segment:hover {
  filter: brightness(0.85);
}

@media (max-width: 768px) {
  .expiry-section {
    border-width: 1px;
  }
  
  .list-heading {
    font-size: 14px;
    padding: 6px 10px;
  }
  
  .pagination-container {
    padding: 6px 8px;
  }

  .chart-container {
    flex-direction: column;
    padding: 10px;
  }

  .legend {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  .chart-tooltip {
    font-size: 12px;
    padding: 6px 12px;
  }
}
</style>